import {
  LabelWithRole,
  MentionInput,
  Ownership,
  ReviewInput,
} from '@skop-app/lib-domain/types'
import { ElementStatus, ReviewStatus, ReviewType } from '@skop-app/lib-types'
import { v7 as uuid } from 'uuid'

import type { CreateOptionInput, Metadata } from '@/services/property'
import { humanizeReviewFieldKey } from '@/utils/reviews'

type GetElementCreationStatusParams = {
  ownership: Ownership
  listOrganizationID: string | undefined
  isAdmin: boolean
  isSkopAdmin: boolean
}

export type ElementCreationResult = {
  status: ElementStatus
  reviewStatus: ReviewStatus
}

/**
 * Determines the status and review requirements for a new element
 * Unified logic for options and references
 */
export const getElementCreationStatus = ({
  ownership,
  listOrganizationID = process.env.NEXT_PUBLIC_SKOP_ORGID,
  isAdmin,
  isSkopAdmin,
}: GetElementCreationStatusParams): ElementCreationResult => {
  // Check if the organization is the owner of the list and the user is an admin
  const isOwnerOrgAdmin =
    isAdmin && ownership.organizationID === listOrganizationID
  // Determine the initial status of the element (new rules)
  const status: ElementStatus =
    isSkopAdmin || isOwnerOrgAdmin
      ? ElementStatus.ACCEPTED
      : ElementStatus.PENDING
  // Determine the review status
  const reviewStatus: ReviewStatus =
    isSkopAdmin || isOwnerOrgAdmin
      ? ReviewStatus.ACCEPTED
      : ReviewStatus.PENDING

  return { status, reviewStatus }
}

type CreateReviewMetadataParams = {
  inputValue: string
  field: string
  reviewType: ReviewType
  organizationID?: string
  parent?: { label: string; id: string }
  fieldOptionsId?: string
}
/**
 * Creates common review metadata
 */
export const createReviewMetadata = ({
  inputValue,
  field,
  reviewType,
  organizationID = process.env.NEXT_PUBLIC_SKOP_ORGID,
  parent,
  fieldOptionsId,
}: CreateReviewMetadataParams) => {
  const baseMetadata = [
    {
      type: 'boolean',
      key: 'isCreationReview',
      value: 'true',
    },
    {
      type: 'label',
      key: 'title',
      value: inputValue, // Review title to display
    },
    {
      type: 'field',
      key: 'colId',
      value: field, // Column ID
    },
    {
      type: 'field',
      key: 'fieldKey',
      value: field, // Stable field identifier
    },
    {
      type: 'label',
      key: 'labelSnapshot',
      value: humanizeReviewFieldKey(field),
    },
    {
      type: 'id',
      key: 'organizationID',
      value: organizationID, // Element's organization ID for SKOP restriction by review
    },
  ]

  // Option-specific metadata
  if (reviewType === ReviewType.REVIEW_OPTION && parent) {
    return [
      ...baseMetadata,
      {
        type: 'id',
        key: 'fieldOptionsId',
        value: fieldOptionsId ?? parent.id, // Option list to know which option list is being targeted by review
      },
      {
        type: 'label',
        key: 'subtitle',
        value: parent.label, // Option list label to display in the review subtitle
      },
      {
        type: 'label',
        key: 'subtitleSnapshot',
        value: parent.label,
      },
    ]
  }

  return baseMetadata
}

export type BuildOptionInputParams = {
  elementID: string
  inputValue: string
  ownership: Ownership
  status: ElementStatus
  parent: { id: string; label: string }
  metadata?: Metadata[]
  value?: string
  targetOrganizationID?: string
  fieldOptionsId?: string | null
  optionOptionsId?: string | null
  group?: string[]
  review?: ReviewInput
}

export type BuildOptionInputResult = CreateOptionInput & {
  review?: ReviewInput
}

export type BuildCreationReviewInputParams = {
  reviewID: string
  elementID: string
  inputValue: string
  field: string
  reviewType: ReviewType
  ownership: Ownership
  reviewStatus: ReviewStatus
  listOrganizationID?: string
  targetOrganizationID?: string
  parent?: { id: string; label: string }
  fieldOptionsId?: string
}

export const buildCreationReviewInput = ({
  reviewID,
  elementID,
  inputValue,
  field,
  reviewType,
  ownership,
  reviewStatus,
  listOrganizationID = process.env.NEXT_PUBLIC_SKOP_ORGID,
  targetOrganizationID,
  parent,
  fieldOptionsId,
}: BuildCreationReviewInputParams): ReviewInput => {
  const organizationID =
    targetOrganizationID ||
    ownership.organizationID ||
    process.env.NEXT_PUBLIC_SKOP_ORGID ||
    ''

  return {
    id: reviewID,
    type: reviewType,
    status: reviewStatus,
    elementID,
    byUserID: ownership.byUserID,
    organizationID: listOrganizationID,
    metadata: createReviewMetadata({
      inputValue,
      field,
      reviewType,
      organizationID,
      fieldOptionsId,
      parent,
    }),
  }
}

/**
 * Build a CreateOptionInput (or CreateFieldWithReviewInput) and its review using unified logic.
 * Pure utility: no side-effects. Callers are responsible for executing mutations and messages.
 */
export const buildOptionInput = (
  params: BuildOptionInputParams,
): BuildOptionInputResult => {
  const {
    elementID,
    inputValue,
    ownership,
    status,
    metadata,
    value,
    targetOrganizationID,
    fieldOptionsId,
    optionOptionsId,
    group,
    review,
  } = params

  const organizationID =
    targetOrganizationID ||
    ownership.organizationID ||
    process.env.NEXT_PUBLIC_SKOP_ORGID ||
    ''

  const newGroup = group && group?.length > 1 ? group.slice(0, -1) : group || []

  const parentInput =
    typeof optionOptionsId === 'string'
      ? {
          optionOptionsId,
          fieldOptionsId: null,
        }
      : {
          fieldOptionsId: fieldOptionsId ?? null,
          optionOptionsId: undefined,
        }

  const optionInput: BuildOptionInputResult = {
    id: elementID,
    label: inputValue,
    value: value || inputValue,
    userID: ownership.byUserID,
    ...parentInput,
    organizationID,
    status,
    group: [...newGroup, inputValue],
    ...(metadata && metadata.length > 0 ? { metadata } : {}),
    ...(review ? { review } : {}),
  }

  return optionInput
}

type BuildMentionsParams = {
  members: LabelWithRole[]
  ownership: Ownership
  messageID: string
}

type BuildMentionTargetsParams = Omit<BuildMentionsParams, 'messageID'>

export const buildMentionTargets = ({
  members,
  ownership,
}: BuildMentionTargetsParams): LabelWithRole[] =>
  members?.filter(
    (member) =>
      (member.role === 'ADMIN' || member.role === 'CREATOR') &&
      member.value !== ownership.byUserID,
  ) ?? []

// Build mentions for the review message
export const buildMentions = ({
  members,
  ownership,
  messageID,
}: BuildMentionsParams): MentionInput[] => {
  return buildMentionTargets({ members, ownership }).map((admin) => ({
    id: uuid(),
    userMentionsId: admin.value.toString(),
    messageMentionsId: messageID,
    role: admin.role,
  }))
}

// ---------------------------------------------------------------------------
// Shared helpers for creating reviews (object + message) for options/references
// ---------------------------------------------------------------------------

export type UpsertReviewParams = {
  upsertType: 'create' | 'update'
  // Input shape is intentionally loose here because different callers
  // (hooks, grid handlers) may enrich the Review object differently.
  // The helper is only responsible for providing the core fields.
  input: any
}

export type UpsertReviewMessageParams = {
  upsertType: 'create' | 'update'
  // Same idea as UpsertReviewParams: callers can extend the message input
  // (e.g. attach productID) without breaking this helper.
  input: any
  mentions: MentionInput[]
}

export type CreateElementReviewParams = {
  elementID: string
  title: string
  message: string
  field: string
  reviewType: ReviewType
  reviewStatus: ReviewStatus
  ownership: Ownership
  teammates: LabelWithRole[]
  parent?: { id: string; label: string }
  onUpsertReview: (params: UpsertReviewParams) => Promise<void> | void
  onUpsertReviewMessage: (
    params: UpsertReviewMessageParams,
  ) => Promise<void> | void
}

/**
 * Create a review (object + message) for an element (option or reference)
 * using unified logic. Pure utility: callers provide mutation callbacks.
 */
export const createElementReview = async ({
  elementID,
  title,
  message,
  field,
  reviewType,
  reviewStatus,
  ownership,
  teammates,
  parent,
  onUpsertReview,
  onUpsertReviewMessage,
}: CreateElementReviewParams): Promise<{
  reviewID: string
  messageID: string
}> => {
  const reviewID = uuid()
  const messageID = uuid()

  const organizationID =
    ownership.organizationID || process.env.NEXT_PUBLIC_SKOP_ORGID

  const metadata = createReviewMetadata({
    inputValue: title,
    field,
    reviewType,
    organizationID,
    parent,
  })

  // 1. Create the Review object
  await onUpsertReview({
    upsertType: 'create',
    input: {
      id: reviewID,
      type:
        reviewType === ReviewType.REVIEW_REFERENCE
          ? 'REVIEW_REFERENCE'
          : 'REVIEW_OPTION',
      status: reviewStatus,
      elementID,
      byUserID: ownership.byUserID ?? '',
      organizationID,
      metadata,
    },
  })

  // 2. Create the review message (with mentions)
  const mentions = buildMentions({
    members: teammates,
    ownership,
    messageID,
  })

  await onUpsertReviewMessage({
    upsertType: 'create',
    input: {
      id: messageID,
      reviewID,
      userID: ownership.byUserID ?? '',
      status: 'PENDING',
      message,
    },
    mentions,
  })

  return { reviewID, messageID }
}
